import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Row, Col, Card, Tag, Spin, Empty, Pagination, Select, message } from 'antd';
import { ClockCircleOutlined, EyeOutlined } from '@ant-design/icons';
import { articleAPI } from '../services/api';
import { PAID_CONTENT_LABEL } from '../constants/labels';
import './Classroom.css';

const { Meta } = Card;
const { Option } = Select;

function Classroom() {
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 12,
    total: 0
  });
  const [selectedCategory, setSelectedCategory] = useState(null);

  useEffect(() => {
    loadCategories();
  }, []);

  useEffect(() => {
    loadArticles();
  }, [pagination.current, selectedCategory]);

  const loadCategories = async () => {
    try {
      const res = await articleAPI.getCategories();
      setCategories(res.data);
    } catch (error) {
      message.error('加载分类失败');
    }
  };

  const loadArticles = async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.current,
        limit: pagination.pageSize,
        ...(selectedCategory && { category_id: selectedCategory })
      };
      const res = await articleAPI.getArticles(params);
      setArticles(res.data.articles);
      setPagination(prev => ({
        ...prev,
        total: res.data.pagination.total
      }));
    } catch (error) {
      message.error('加载文章失败');
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page) => {
    setPagination(prev => ({ ...prev, current: page }));
  };

  const handleCategoryChange = (value) => {
    setSelectedCategory(value);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleArticleClick = (id) => {
    navigate(`/classroom/${id}`);
  };

  return (
    <div className="classroom-container">
      <div className="classroom-header">
        <h1 className="page-title">{PAID_CONTENT_LABEL}</h1>
        <p className="page-subtitle">精选付费资讯，覆盖心理健康与情绪调节主题</p>
      </div>

      <div className="classroom-filters">
        <Select
          placeholder="选择分类"
          style={{ width: 200 }}
          onChange={handleCategoryChange}
          allowClear
        >
          {categories.map(cat => (
            <Option key={cat.id} value={cat.id}>{cat.name}</Option>
          ))}
        </Select>
      </div>

      {loading ? (
        <div className="loading-container">
          <Spin size="large" tip="加载中..." />
        </div>
      ) : articles.length === 0 ? (
        <Empty description="暂无文章" />
      ) : (
        <>
          <Row gutter={[24, 24]}>
            {articles.map(article => (
              <Col xs={24} sm={12} md={8} lg={6} key={article.id}>
                <Card
                  hoverable
                  cover={
                    <div className="article-cover">
                      <img
                        alt={article.title}
                        src={article.cover_image || '/images/default-article.svg'}
                        onError={(e) => { e.target.src = '/images/default-article.svg'; }}
                      />
                      {article.is_premium === 1 && (
                        <Tag color="gold" className="premium-tag">
                          付费 ¥{article.price}
                        </Tag>
                      )}
                    </div>
                  }
                  onClick={() => handleArticleClick(article.id)}
                >
                  <Meta
                    title={
                      <div className="article-title" title={article.title}>
                        {article.title}
                      </div>
                    }
                    description={
                      <div className="article-meta">
                        <div className="article-summary">
                          {article.summary || '暂无摘要'}
                        </div>
                        <div className="article-stats">
                          <span>
                            <ClockCircleOutlined /> {new Date(article.created_at).toLocaleDateString()}
                          </span>
                          <span>
                            <EyeOutlined /> {article.views || 0}
                          </span>
                        </div>
                      </div>
                    }
                  />
                </Card>
              </Col>
            ))}
          </Row>

          {pagination.total > pagination.pageSize && (
            <div className="pagination-container">
              <Pagination
                current={pagination.current}
                pageSize={pagination.pageSize}
                total={pagination.total}
                onChange={handlePageChange}
                showSizeChanger={false}
                showTotal={(total) => `共 ${total} 篇文章`}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default Classroom;
